import { useEffect, useRef, useState } from 'react';
import type { Choice } from '@pons/contracts';
import type { CoinView } from '../arena/model';
import {
  GAME_VOTE_MS,
  GAME_ROUND_MS,
  type GameSnapshot,
  type SharedGameRound,
} from '../../../../packages/contracts/src/game';

export function gameCoin(
  round: SharedGameRound,
  now: number,
  stale = false,
): CoinView {
  const current = round.history.at(-1) ?? round.cap;
  return {
    id: round.id,
    name: round.name,
    symbol: round.symbol,
    address: null,
    pairSymbol: 'GAME',
    pairHref: null,
    links: [],
    marketCap: current,
    graduationCap: round.cap,
    baseline: round.cap,
    changePercent: (current / round.cap - 1) * 100,
    resultPercent: round.outcome ? (current / round.cap - 1) * 100 : null,
    history: round.history,
    start: round.start,
    closesAt: round.start + GAME_VOTE_MS,
    settlesAt: round.start + GAME_ROUND_MS,
    phase: round.outcome
      ? 'settled'
      : now >= round.start + GAME_VOTE_MS
        ? 'watching'
        : now < round.start
          ? 'opening'
          : 'open',
    choice: round.choice,
    outcome: round.outcome,
    votes: round.votes,
    votesUp: round.votesUp,
    split: round.votes ? (round.votesUp / round.votes) * 100 : null,
    simulated: true,
    stale,
    note: null,
  };
}

export function useGame(accountId?: string) {
  const [snapshot, setSnapshot] = useState<GameSnapshot>();
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [clock, setClock] = useState(Date.now);
  const sync = useRef({ at: 0, serverTime: 0 });
  const identity = useRef(accountId);
  const accepted = useRef(new Map<string, Choice>());
  identity.current = accountId;
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setSnapshot(undefined);
    accepted.current.clear();
    sync.current = { at: 0, serverTime: 0 };
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const response = await fetch('/api/game/rounds', {
          cache: 'no-store',
          headers: accountId ? { 'X-Pons-Account': accountId } : {},
          signal: AbortSignal.any([
            controller.signal,
            AbortSignal.timeout(8000),
          ]),
        });
        if (!response.ok) throw Error('Game feed is temporarily unavailable.');
        const body: GameSnapshot = await response.json();
        if (!Array.isArray(body.rounds) || !Number.isFinite(body.serverTime))
          throw Error('Game feed is temporarily unavailable.');
        if (active) {
          sync.current = { at: Date.now(), serverTime: body.serverTime };
          setSnapshot({
            ...body,
            rounds: body.rounds.map((round) => {
              const choice = accepted.current.get(round.id);
              if (round.choice) accepted.current.delete(round.id);
              return choice && !round.choice ? { ...round, choice } : round;
            }),
          });
          setError('');
        }
      } catch {
        if (active) setError('Game feed is temporarily unavailable.');
      } finally {
        if (active)
          timer = setTimeout(() => void poll(), document.hidden ? 5000 : 1000);
      }
    };
    void poll();
    const clockTimer = setInterval(() => setClock(Date.now()), 250);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
      clearInterval(clockTimer);
    };
  }, [accountId]);
  const now = sync.current.serverTime
    ? sync.current.serverTime + Math.max(0, clock - sync.current.at)
    : clock;
  const stale = !snapshot || clock - sync.current.at > 5000;
  async function vote(id: string, choice: Choice) {
    if (stale || pending) return false;
    setPending(true);
    try {
      const response = await fetch('/api/game/predictions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accountId ? { 'X-Pons-Account': accountId } : {}),
        },
        body: JSON.stringify({ id, choice }),
        signal: AbortSignal.timeout(8000),
      });
      const body = await response.json();
      if (!response.ok)
        throw Error(body.error || 'Prediction could not be saved.');
      if (identity.current === accountId) {
        accepted.current.set(id, choice);
        setSnapshot((old) =>
          old
            ? {
                ...old,
                rounds: old.rounds.map((r) =>
                  r.id === id ? { ...r, choice } : r,
                ),
              }
            : old,
        );
      }
      return true;
    } catch (cause) {
      if (identity.current === accountId)
        setError(
          cause instanceof Error
            ? cause.message
            : 'Prediction could not be saved.',
        );
      return false;
    } finally {
      setPending(false);
    }
  }
  return {
    coins: (snapshot?.rounds || []).map((r) => gameCoin(r, now, stale)),
    rounds: snapshot?.rounds || [],
    stats: snapshot?.stats,
    ready: !!snapshot,
    error,
    now,
    vote,
  };
}
